import { useEffect, useRef } from "react";

const COLS = ["view", "edit", "export"];

const AccessLevelTable = ({ modules, value, onChange }) => {
  const headerRefs = {
    view: useRef(null),
    edit: useRef(null),
    export: useRef(null),
  };

  const allChecked = (col) => modules.every((m) => value?.[m.key]?.[col]);
  const someChecked = (col) =>
    modules.some((m) => value?.[m.key]?.[col]) && !allChecked(col);

  useEffect(() => {
    COLS.forEach((c) => {
      if (headerRefs[c].current) {
        headerRefs[c].current.indeterminate = someChecked(c);
      }
    });
  }, [modules, value]);

  const toggleAllInCol = (col, checked) => {
    const next = { ...(value || {}) };

    modules.forEach((m) => {
      next[m.key] = {
        view: false,
        edit: false,
        export: false,
        ...(next[m.key] || {}),
      };

      next[m.key][col] = checked;

      // If selecting all Edit or Export → must also select View
      if ((col === "edit" || col === "export") && checked) {
        next[m.key].view = true;
      }

      // If unselecting all View → must also unselect Edit & Export
      if (col === "view" && !checked) {
        next[m.key].edit = false;
        next[m.key].export = false;
      }
    });

    onChange(next);
  };

  const toggleCell = (module, col, checked) => {
    const next = {
      ...(value || {}),
      [module]: {
        view: false,
        edit: false,
        export: false,
        ...(value?.[module] || {}),
      },
    };

    next[module][col] = checked;

    // Rule 1 & 2: If edit or export is checked, enable view automatically
    if ((col === "edit" || col === "export") && checked) {
      next[module].view = true;
    }

    // Rule 3: If view is unchecked, also uncheck edit & export
    if (col === "view" && !checked) {
      next[module].edit = false;
      next[module].export = false;
    }

    onChange(next);
  };

  return (
    <div className="mt-6 w-full">
      {/* Mobile layout (default) */}
      <div className="block md:hidden">
        {/* Header Row - table-like */}
        <div className="flex border-b mb-4">
          <div className="w-1/2 text-left text-gray-700 font-medium text-sm py-2">Select access level</div>
          <div className="w-1/2 text-left">
            {COLS.map((c) => (
              <div key={c} className="flex items-center gap-2 mb-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    ref={headerRefs[c]}
                    type="checkbox"
                    checked={allChecked(c)}
                    onChange={(e) => toggleAllInCol(c, e.target.checked)}
                    className="peer sr-only"
                  />
                  <span className="h-4 w-4 flex items-center justify-center rounded border border-gray-300 peer-checked:bg-[#05E27E] peer-checked:border-[#05E27E]">
                    <svg className="w-3 h-3" fill="none" stroke="white" strokeWidth="3" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  <span className="font-medium text-gray-700 text-sm">
                    {c === "view" ? "Select all (view only)" : c === "edit" ? "Select all (Edit)" : "Select all (Export)"}
                  </span>
                </label>
              </div>
            ))}
          </div>
        </div>
        {/* Rows - table-like */}
        {modules.map((m) => (
          <div key={m.key} className="flex border-b py-3">
            <div className="w-1/2 text-left font-semibold text-gray-700 text-sm">{m.label}</div>
            <div className="w-1/2 text-left">
              {COLS.map((c) => (
                <div key={c} className="flex items-center gap-2 mb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!value?.[m.key]?.[c]}
                      onChange={(e) => toggleCell(m.key, c, e.target.checked)}
                      className="peer sr-only"
                    />
                    <span className="h-4 w-4 flex items-center justify-center rounded border border-gray-300 peer-checked:bg-[#05E27E] peer-checked:border-[#05E27E]">
                      <svg className="w-3 h-3" fill="none" stroke="white" strokeWidth="3" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                    <span className="font-normal text-gray-700 text-sm">
                      {c === "view" ? "View only" : c === "edit" ? "Edit" : "Export"}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {/* Desktop layout (md+) */}
      <div className="hidden md:block">
        {/* Header Row */}
        <div className="grid grid-cols-[3fr_1.32fr_1fr_1.1fr] mb-4 text-sm text-gray-700 font-medium items-center">
          <div className="text-gray-900">Select access level</div>
          {COLS.map((c) => (
            <label key={c} className="flex items-center justify-center gap-2 cursor-pointer w-full h-full">
              <input
                ref={headerRefs[c]}
                type="checkbox"
                checked={allChecked(c)}
                onChange={(e) => toggleAllInCol(c, e.target.checked)}
                className="peer sr-only"
              />
              <span className="h-4 w-4 flex items-center justify-center rounded border border-gray-300 peer-checked:bg-[#05E27E] peer-checked:border-[#05E27E]">
                <svg className="w-3 h-3" fill="none" stroke="white" strokeWidth="3" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </span>
              <span className="font-medium text-gray-700 text-sm">
                {c === "view" ? "Select all" : c === "edit" ? "Select all" : "Select all"}
              </span>
            </label>
          ))}
        </div>
        {/* Rows */}
        {modules.map((m) => (
          <div key={m.key} className="grid grid-cols-[3fr_0.9fr_0.94fr_1.16fr] items-center py-2 border-b">
            <div className="font-medium text-gray-700 text-[16px]">{m.label}</div>
            {COLS.map((c) => (
              <label key={c} className="flex items-center justify-center gap-2 cursor-pointer w-full h-full">
                <input
                  type="checkbox"
                  checked={!!value?.[m.key]?.[c]}
                  onChange={(e) => toggleCell(m.key, c, e.target.checked)}
                  className="peer sr-only"
                />
                <span className="h-4 w-4 flex items-center justify-center rounded border border-gray-300 peer-checked:bg-[#05E27E] peer-checked:border-[#05E27E]">
                  <svg className="w-3 h-3" fill="none" stroke="white" strokeWidth="3" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <span className="font-normal text-gray-700 text-sm">
                  {c === "view" ? "View only" : c === "edit" ? "Edit" : "Export"}
                </span>
              </label>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AccessLevelTable;